import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Radio,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDispatch, useSelector } from "react-redux";
import {
  resetSellerStatusUpdate,
  SELLER_STATUS_UPDATE_ACTIONS,
  setSelectedStatus,
} from "../../redux/slices/sellerSlice";
import { APPROVESELLER, BUILDING, CERTIFIED, PLACEONHOLD, REJECTSELLER, STATUSES } from "../../utils/constants";
import { getDefaultValueStatus } from "../../utils";

const CustomDropdown = ({ handleConfirm }) => {
  const dispatch = useDispatch();
  const { selectedSeller, selectedStatus, sellerStatusUpdate } = useSelector((state) => state.seller);

  const [anchorEl, setAnchorEl] = useState(null);
  const [shouldCloseAfterApi, setShouldCloseAfterApi] = useState(false);
  const open = Boolean(anchorEl);

  const sellerStatus = selectedSeller?.status_type;
  const isPendingApproval = sellerStatus === "PR";

  const ActionButton = ({ label, onClick }) => (
  <Button
    sx={{
      borderRadius: "24px",
      maxWidth: '157px',
      minWidth: '120px',
      p: '12px 32px',
      maxHeight: '42px',
      whiteSpace: 'nowrap',
      fontSize: "16px",
      fontWeight: 700,
      textTransform: "none",
      bgcolor: "#1460AA",
      color: "#fff",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#1460AA",
        boxShadow: "none",
      },
    }}
    onClick={onClick}
  >
    {label}
  </Button>
);

  const options = useMemo(() => STATUSES[sellerStatus] || [], [sellerStatus]);

  const currentValue = selectedStatus === 0 && isPendingApproval?"Pending Approval": selectedStatus || getDefaultValueStatus(sellerStatus) || "";

  const defaultStatus = getDefaultValueStatus(sellerStatus);
  const showApproveButton = selectedStatus === APPROVESELLER && defaultStatus !== APPROVESELLER;
  const showCertifyButton = selectedStatus === CERTIFIED && defaultStatus !== CERTIFIED;
  const showBuildingButton = selectedStatus === BUILDING && defaultStatus !== BUILDING;
  
  useEffect(()=>{
    dispatch(setSelectedStatus({ data: getDefaultValueStatus(sellerStatus) }))
  },[dispatch, sellerStatus])

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShouldCloseAfterApi(false);
    dispatch(resetSellerStatusUpdate());
    dispatch(setSelectedStatus({ data: getDefaultValueStatus(selectedSeller?.status_type) }));
  };

  const handleSelect = (value) => {
    if ([REJECTSELLER, PLACEONHOLD].includes(value)) {
      setShouldCloseAfterApi(true);
    } else {
      setShouldCloseAfterApi(false);
    }
    dispatch(resetSellerStatusUpdate());
    dispatch(setSelectedStatus({ data: value }));    
  };

  const handleConfirmAndClose = () => {
    setShouldCloseAfterApi(true);
    dispatch(resetSellerStatusUpdate());
    handleConfirm();
  };

  useEffect(() => {
    if (!shouldCloseAfterApi) return;

    if (sellerStatusUpdate.inProgress) return;

    const eligibleActions = [
      SELLER_STATUS_UPDATE_ACTIONS.APPROVE,
      SELLER_STATUS_UPDATE_ACTIONS.CERTIFY,
      SELLER_STATUS_UPDATE_ACTIONS.BUILD,
      SELLER_STATUS_UPDATE_ACTIONS.ON_HOLD,
      SELLER_STATUS_UPDATE_ACTIONS.REJECT,
    ];

    if (
      sellerStatusUpdate.isSuccess &&
      eligibleActions.includes(sellerStatusUpdate.actionType)
    ) {
      setAnchorEl(null);
      setShouldCloseAfterApi(false);
      dispatch(setSelectedStatus({ data: getDefaultValueStatus(selectedSeller?.status_type) }));
      dispatch(resetSellerStatusUpdate());
      return;
    }

    if (sellerStatusUpdate.isError) {
      setShouldCloseAfterApi(false);
      dispatch(resetSellerStatusUpdate());
    }
  }, [dispatch, selectedSeller?.status_type, sellerStatusUpdate, shouldCloseAfterApi]);

  useEffect(() => {
    if (!shouldCloseAfterApi) return;

    if (selectedStatus === defaultStatus) {
      setShouldCloseAfterApi(false);
    }
  }, [defaultStatus, selectedStatus, shouldCloseAfterApi]);

  const getColor = {
    "PR": "#FFCF4D",
    "AE": "#20842F",
    "APA": "#20842F",
    "OH": "#FF9A15",
    "RJ": "#D8292F",
    "OB": "#CFE6FC",
    "BI": "#02778F"

  }
   const getTextColor = {
    "PR": "#000000",
    "AE": "#ffffff",
    "APA": "#ffffff",
    "OH": "#000000",
    "RJ": "#ffffff",
    "OB": "#1460AA",
    "BI": "#ffffff"

  }

  return (
    <>
      <Button
        onClick={handleOpen}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          mb: 1,
          px: "16px",
          py: "8px",
          minWidth: "282px",
          height: "40px",
          borderRadius: "999px",
          textTransform: "none",
          justifyContent: "space-between",
          bgcolor: getColor[sellerStatus],
          color: getTextColor[sellerStatus],
          boxShadow: "0px 0px 15px 0px #00000026",
          fontWeight: 500,
          fontSize:'16px',
          "&:hover": {
            bgcolor: getColor[sellerStatus],
            boxShadow: "0px 0px 15px 0px #00000026",
          },
          "& .MuiButton-endIcon":{
            rotate:`${open?'180deg':'0deg'}`
          }
        }}
      >
        {currentValue || "Select status"}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            width: 282,
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        }}
      >
        

        {options.map((action, idx) => {
          const checked = selectedStatus === action;
          return (
            <Box key={idx}>
            <MenuItem
              key={action}
              onClick={() => handleSelect(action)}
              sx={{
                p:'16px'
               
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Typography sx={{ flex: 1, fontSize:'14px', fontWeight: 500 }}>{action}</Typography>
                <Radio
                  checked={checked}
                  value={action}
                  sx={{
                    p: 0,
                    color: "#1460AA",
                    "&.Mui-checked": { color: "#1460AA" },
                  }}
                />
              </Box>
            </MenuItem>
            <Divider />
            </Box>
          );
        })}

        
        <Box sx={{ display: "flex", justifyContent: (showApproveButton || showCertifyButton || showBuildingButton) ? "space-between" : "flex-end", p:'0px 16px 0px 16px' }}>
          
          {showApproveButton && <ActionButton label="Approve Seller" onClick={handleConfirmAndClose} />}
          {showBuildingButton && <ActionButton label="Build Seller" onClick={handleConfirmAndClose} />}
          {showCertifyButton && <ActionButton label="Certify Seller" onClick={handleConfirmAndClose} />}
          <Button
            onClick={handleClose}
            variant="text"
            disableRipple
            disableTouchRipple
            disableFocusRipple
            sx={{ textTransform: "none", fontWeight: 700, fontSize:'16px', minWidth: 0 }}
          >
            Close
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default CustomDropdown;